// ============================================================================
// BusinessEntityCard — grille cartes metier (GEST CARTES METIER, TEMPS 2)
// ============================================================================
// Shell partage equipes + affichages : nom/titre + meta + chip + drill-in.
// Composant LOCAL gest — candidat @gs/ui si un 3e consommateur hors-gest emerge.
// Regle #4 : zero emoji.

import { type KeyboardEvent, type ReactNode } from 'react';
import { GS_COMPONENTS } from '@gs/ui';

export interface BusinessEntityCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  onActivate: () => void;
  /** Libelle aria pour le drill-in (clic + clavier). */
  activateLabel: string;
  children?: ReactNode;
  meta?: ReactNode;
}

export function BusinessEntityCard({
  title,
  subtitle,
  badge,
  onActivate,
  activateLabel,
  children,
  meta,
}: BusinessEntityCardProps) {
  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  }

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={activateLabel}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      className={`${GS_COMPONENTS.card} flex min-h-11 cursor-pointer flex-col gap-3 p-4 touch-manipulation transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500`}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</div>
          {subtitle != null ? (
            <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
        {badge != null ? <div className="shrink-0">{badge}</div> : null}
      </header>
      {children}
      {meta}
    </article>
  );
}

export function BusinessEntityCardMetaRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-3 border-t border-slate-100 pt-3 dark:border-slate-700">
      {children}
    </div>
  );
}

export interface BusinessEntityCardMetaItemProps {
  label: string;
  children: ReactNode;
  className?: string;
}

export function BusinessEntityCardMetaItem({
  label,
  children,
  className = '',
}: BusinessEntityCardMetaItemProps) {
  return (
    <div className={`min-w-0 ${className}`.trim()}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <div className="mt-0.5 text-meta font-semibold tabular-nums text-slate-700 dark:text-slate-200">
        {children}
      </div>
    </div>
  );
}
